/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { keyframes } from '@emotion/react'
import colors from '@sportninja/common/constants/appColors'

// SECONDARY_350 is dropped from the inferred type of the JS appColors module,
// so read it through a narrow string-record cast — the same pattern as
// PlayerFormSkeleton and RegistrationStartSkeleton.
const palette = colors as Record<string, string>

interface RepPaymentStatusSkeletonProps {
  /** Which half of the status row it stands in for. */
  line: 'title' | 'description'
}

/**
 * Sep 24 — the confirmation step's payment status while the fresh session loads.
 *
 * The wizard's own copy of the session predates the pay page, so its answer
 * ("nothing has been charged today") may no longer be true: the row says nothing
 * until the server's does. One bar per line of the row it replaces. Decorative, so
 * hidden from screen readers.
 */
const RepPaymentStatusSkeleton = ({ line }: RepPaymentStatusSkeletonProps) => (
  <span
    css={line === 'title' ? titleBarStyle : descriptionBarStyle}
    aria-hidden='true'
    data-testid={`rep-payment-skeleton-${line}`}
  />
)

export default RepPaymentStatusSkeleton

const pulse = keyframes`
  0% { opacity: 0.5; }
  50% { opacity: 0.9; }
  100% { opacity: 0.5; }
`

/* The house skeleton bar — see PlayerFormSkeleton. */
const bar = css`
  display: block;
  border-radius: 8px;
  background-color: ${palette.SECONDARY_350};
  animation: ${pulse} 1.4s ease-in-out infinite;
`

/* The status title is one paragraphSmall line. */
const titleBarStyle = css`
  ${bar}
  width: 240px;
  max-width: 100%;
  height: 16px;
  margin-top: 4px;
`

const descriptionBarStyle = css`
  ${bar}
  width: 160px;
  max-width: 100%;
  height: 12px;
  margin-top: 4px;
`
